import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Evacuation,
  EvacuationLiveMessage,
  EvacuationPoint,
  EvacuationMarkResult,
  EvacuationReport,
  EvacuationStartResult,
  EvacuationView,
  FirePanel,
  Group,
  Member,
  MemberPointOverrideList,
  page,
} from '@cloudsign/shared';
import type { RollCallPerson, RollCallStatus } from '@cloudsign/shared';
import { z } from 'zod';
import { ApiClientError, api } from '../../lib/api';

export const evacKey = ['evacuation'] as const;
export const activeKey = (siteId: string | null) => [...evacKey, 'active', siteId] as const;
export const pointsKey = (siteId: string) => [...evacKey, 'points', siteId] as const;

const PointList = z.array(EvacuationPoint);
const FirePanelList = z.array(FirePanel);
const MemberPage = page(Member);
const GroupList = z.array(Group);

/** A fresh version-4 id for an idempotency key (works where `crypto.randomUUID` is missing). */
export function newId(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (c?.randomUUID) return c.randomUUID();
  const hex = (n: number) => Array.from({ length: n }, () => Math.floor(Math.random() * 16).toString(16)).join('');
  return `${hex(8)}-${hex(4)}-4${hex(3)}-${(8 + Math.floor(Math.random() * 4)).toString(16)}${hex(3)}-${hex(12)}`;
}

/** The running evacuation at a site, or null when there is none (the server answers 404). */
export async function fetchActive(siteId: string): Promise<EvacuationView | null> {
  try {
    return await api.get('/evacuation/active', { query: { siteId }, schema: EvacuationView });
  } catch (e) {
    if (e instanceof ApiClientError && e.status === 404) return null;
    throw e;
  }
}

export function useActive(siteId: string | null) {
  return useQuery({ queryKey: activeKey(siteId), enabled: Boolean(siteId), queryFn: () => fetchActive(siteId as string) });
}

export const memberPointsKey = (memberId: string) => [...evacKey, 'member-points', memberId] as const;

/** The member's own muster point at each site (sites on the default are not listed), as siteId to pointId. */
export function useMemberPoints(memberId: string) {
  return useQuery({
    queryKey: memberPointsKey(memberId),
    queryFn: async () => {
      const list = await api.get(`/evacuation/members/${memberId}/point`, { schema: MemberPointOverrideList });
      return Object.fromEntries(list.map((o) => [o.siteId, o.pointId])) as Record<string, string | null>;
    },
  });
}

export function usePoints(siteId: string | null) {
  return useQuery({
    queryKey: pointsKey(siteId ?? ''),
    enabled: Boolean(siteId),
    queryFn: () => api.get('/evacuation/points', { query: { siteId: siteId as string }, schema: PointList }),
  });
}

/** Applies a live message to the cached roll call. Returns null when the message says to refetch instead. */
export function applyLive(view: EvacuationView | null | undefined, data: unknown): EvacuationView | null | 'refetch' {
  const parsed = EvacuationLiveMessage.safeParse(data);
  if (!parsed.success) return 'refetch';
  const m = parsed.data;
  if (m.type === 'refresh') return 'refetch';
  if (m.type === 'evac.state') {
    if (m.evacuation.status === 'ended') return null;
    return view ? { ...view, evacuation: m.evacuation } : 'refetch';
  }
  if (!view) return 'refetch';
  const exists = view.people.some((p) => p.id === m.person.id);
  const people = exists ? view.people.map((p) => (p.id === m.person.id ? m.person : p)) : [...view.people, m.person];
  return { ...view, people, evacuation: { ...view.evacuation, counts: countsOf(people) } };
}

/** Counts kept in step with the people while live updates arrive. */
export function countsOf(people: readonly RollCallPerson[]): Evacuation['counts'] {
  const c = { total: people.length, unaccounted: 0, safe: 0, missing: 0, offSite: 0 };
  for (const p of people) {
    if (p.status === 'unaccounted') c.unaccounted += 1;
    else if (p.status === 'safe') c.safe += 1;
    else if (p.status === 'missing') c.missing += 1;
    else c.offSite += 1;
  }
  return c;
}

export function useStartEvacuation(siteId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (kind: 'real' | 'drill') => api.post('/evacuation/start', { body: { siteId, kind }, schema: EvacuationStartResult }),
    onSuccess: () => qc.invalidateQueries({ queryKey: activeKey(siteId) }),
  });
}

export function useEndEvacuation(siteId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post(`/evacuation/${id}/end`, { schema: Evacuation }),
    onSuccess: () => qc.invalidateQueries({ queryKey: activeKey(siteId) }),
  });
}

export interface MarkInput {
  evacuationId: string;
  person: RollCallPerson;
  status: RollCallStatus;
  pointId: string | null;
}

/** Marks one person. The answer carries the person as the server now holds them, which replaces the cached row. */
export function useMarkPerson(siteId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (m: MarkInput) =>
      api.post(`/evacuation/${m.evacuationId}/marks`, {
        body: { idempotencyKey: newId(), personId: m.person.id, status: m.status, pointId: m.pointId, occurredAt: new Date().toISOString() },
        schema: EvacuationMarkResult,
      }),
    onSuccess: (r) => {
      qc.setQueryData<EvacuationView | null>(activeKey(siteId), (cur) => {
        if (!cur) return cur;
        const people = cur.people.map((p) => (p.id === r.person.id ? r.person : p));
        return { ...cur, people, evacuation: { ...cur.evacuation, counts: countsOf(people) } };
      });
    },
  });
}

export function useFirePanels() {
  return useQuery({ queryKey: [...evacKey, 'panels'], queryFn: () => api.get('/evacuation/fire-panels', { schema: FirePanelList }) });
}

export function useReport(id: string) {
  return useQuery({ queryKey: [...evacKey, 'report', id], queryFn: () => api.get(`/evacuation/report/${id}`, { schema: EvacuationReport }) });
}

export function useEvacuationView(id: string) {
  return useQuery({ queryKey: [...evacKey, 'view', id], queryFn: () => api.get(`/evacuation/${id}`, { schema: EvacuationView }) });
}

export function useRepeatGroups() {
  return useQuery({
    queryKey: [...evacKey, 'groups'],
    queryFn: async () => (await api.get('/groups', { query: { status: 'active' }, schema: GroupList })).filter((g) => g.kind === 'repeat'),
  });
}

export function useGroupMembers(groupId: string, search: string) {
  return useQuery({
    queryKey: [...evacKey, 'members', groupId, search],
    enabled: groupId !== '',
    queryFn: () => api.get('/members', { query: { groupId, status: 'current', search, pageSize: 20 }, schema: MemberPage }),
  });
}

export function useMemberName(id: string) {
  return useQuery({ queryKey: [...evacKey, 'member', id], queryFn: () => api.get(`/members/${id}`, { schema: Member }), staleTime: 300_000 });
}


/** "7 min 30 s" for a number of seconds. */
export function durationText(seconds: number | null, t: (key: string, params?: Record<string, string | number>) => string): string {
  if (seconds === null) return '';
  const s = Math.max(0, Math.round(seconds));
  const m = Math.floor(s / 60);
  if (m === 0) return t('evac.seconds', { s });
  const rest = s % 60;
  return rest === 0 ? t('evac.minutes', { m }) : t('evac.minutesSeconds', { m, s: rest });
}
